import { useState, useEffect, useMemo } from 'react';
import { Button, Segment, SegmentGroup, Message, Icon, Popup } from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import FlexBoxTmp from '../../../components/FlexBox';
import Loading from '../../../components/Loading';
import PassFailResult from '../../../components/PassFailResult';
import CenterCell from '../../../components/table/CenterCell';
import RightCell from '../../../components/table/RightCell';
import { getAPI } from '../../../libs/fetchAPI';
import BasicPaginatedTable from '../../../components/table/BasicPaginatedTable';
import Duration from '../../../components/Duration';
import SingleDetailsModal from '../../../components/SingleDetailsModal';
// import IntroMessage from '../../../components/IntroMessage';
import JiraLink from '../../../components/JiraLink';

const FlexBox = styled(FlexBoxTmp)`
	justify-content: space-evenly;
	text-align: center;
`;

const Header = styled.h5`
	padding: 0;
	margin: 0 0 5px 0;
`;

const CustomSegment = styled(Segment)`
	/*margin-left: -10px !important;
	margin-right: -10px !important;*/
	margin: 0 !important;

`;

const CustomSuiteTitle = styled.span`
	max-width: 75px !important;
`;

const CustomCenterCell = styled(CenterCell)`
	min-width: 250px;
`;

function SingleRun({ handleSetName }) {
	const { id }                                      = useParams();
	const [ loading, setLoading ]                     = useState(true);
	const [ loading_details, setLoadingDetails ]      = useState({});
	const [ run, setRun ]                             = useState([]);
	const [ specs, setSpecs]                          = useState([]);
	const [ tests, setTests ]                         = useState([]);
	const [ test_errors, setTestErrors ]              = useState([]);
	const [ artifacts, setArtifacts ]                 = useState([]);
	const [ show_details_modal, setShowDetailsModal ] = useState(false);

	/* eslint-disable */
	useEffect(() => {
		if(id !== `none`) {
			setLoading(true);

			Promise.all([getAPI(`/runs/${id}`), getAPI(`/runs/${id}/suites?limit=10000`)])
				.then((data) => {
					const [ run, suites ] = data;

					setRun(run.data[0]);
					handleSetName(run.data[0].issue_summary);

					// Sort the suites by failures first
					// const sorted_suites = suites.data.sort(a => {
					// 	return Number(a.failed) === 1 ? -1 : 0;
					// });
					const sorted_suites = suites.data.sort((a, b) => {
						// Sort by "failed" first
						if(Number(a.failed) === 1 && Number(b.failed) !== 1) {
							return -1; // a before b
						}
						else if(Number(b.failed) === 1 && Number(a.failed) !== 1) {
							return 1; // b before a
						}

						// If "failed" is the same for both, then sort by "flaky"
						return Number(a.flaky) - Number(b.flaky); // Assumes flaky is also a number or something that can be coerced into one
					});

					setSpecs(sorted_suites);
					setLoading(false);
				});
		}
	}, [id]);
	/* eslint-enable */

	function handleCloseDetails() {
		setShowDetailsModal(false);
	}

	function handleViewDetails(id) {
		setLoadingDetails({ [id] : true });

		Promise.all([
			getAPI(`/suites/${id}/tests`),
			getAPI(`/suites/${id}/errors`),
			getAPI(`/suites/${id}/artifacts`)
		]).then((response) => {
				const [ tmp_tests, tmp_errors, tmp_artifacts ] = response;

				setTests(tmp_tests.data.reverse());
				setTestErrors(tmp_errors.data);
				setArtifacts(tmp_artifacts.data || []);
				setShowDetailsModal(true);
				setLoadingDetails({});
			});
	}

	/* eslint-disable */
	const columns = useMemo(() => [
		{
			Header   : `Spec File`,
			accessor : `spec_file`,
			Cell     : ({ cell }) => {
				if(Number(cell.row.values.organization_id) === 2) {
					return <JiraLink issue_key={cell.value} />
				}

				return cell.value;
			}
		},
		{
			Header    : `Spec Start`,
			accessor  : `start`,
			Cell      : ({ cell }) => <span>{cell.value ? new Date(`${cell.value} UTC`).toLocaleString(`en-US`, {
				hour   : `2-digit`,
				minute : `2-digit`,
				hour12 : true,
			}) : ``}</span>
		},
		{
			Header   : `Suite Title`,
			accessor : `title`,
			Cell     : ({ cell }) => <CustomSuiteTitle>{cell.value ? cell.value.replace(/FOCUS-\d+\s?-\s?/, ``) : ``}</CustomSuiteTitle>
		},
		{
			Header   : `Capabilities`,
			accessor : `capabilities`,
		},
		{
			Header   : `Duration`,
			accessor : `duration`,
			collapse : true,
			Cell     : ({ cell }) => <RightCell><Duration duration={cell.value} /></RightCell>
		},
		{
			Header   : `Retries`,
			accessor : `retries`,
			collapse : true,
			Cell     : ({ cell }) => <RightCell>{cell.value}</RightCell>
		},
		{
			Header   : `Retry %`,
			accessor : `retry_percent`,
			Cell     : ({ cell }) => <RightCell>{cell.value}%</RightCell>
		},
		{
			Header         : `Result`,
			accessor       : `passed`,
			collapse       : true,
			disableFilters : true,
			Cell           : ({ cell }) => {
				if(Number(cell.value) === 1) {
					return <PassFailResult passed={cell.value} skipped={cell.row.values.skipped} />
				}

				return (
					<Popup
						trigger={<span><PassFailResult passed={cell.value} skipped={cell.row.values.skipped} flaky={cell.row.values.flaky} /></span>}
						content={<Message negative><p>{cell.row.values.last_error}</p></Message>}
						on={[`hover`, `click`]}
					/>
				);
			}
		},
		{
			Header   : `Skipped Hidden`,
			accessor : `skipped`,
		},
		{
			Header   : `Flaky Hidden`,
			accessor : `flaky`,
		},
		{
			Header   : `Last Error Hidden`,
			accessor : `last_error`,
		},
		{
			Header   : `Version Hidden`,
			accessor : `version`,
		},
		{
			Header         : ``,
			accessor       : `id`,
			disableFilters : true,
			Cell           : ({ cell }) => {
				const row    = cell.row.values;
				const params = {
					spec_file : row.spec_file,
					version   : row.version || ``,
				};

				const link = `${window.location.origin}/specs/single?${new URLSearchParams(params)}`;

				return (
					<CustomCenterCell>
						<Button loading={loading_details[cell.value]} color="teal" onClick={() => handleViewDetails(cell.value)}>Details</Button>
						<a href={link} target="_blank" rel="noreferrer"><Button primary><span>History</span><span>&nbsp;&nbsp;<Icon name="external" /></span></Button></a>
					</CustomCenterCell>
				);
			}
		}
	], [loading_details, run]);
	/* eslint-enable */

	if(!id || id === `none`) {
		return <Message>No run selected</Message>
	}

	if(loading) {
		return <Loading />
	}

	return (
		<>
			<SingleDetailsModal
				open={show_details_modal}
				handleClose={handleCloseDetails}
				tests={tests}
				errors={test_errors}
				artifacts={artifacts}
			/>

			{/* <IntroMessage
				id={2}
				message='The Test Runs -> Run Specs page lists all the specs that were ran in the selected test run. Clicking the "Details" button will show all tests inside of a suite and any errors found. Clicking the "History" button will show all the history about an individual suite accross all test runs.'
			/> */}

			<RunSegment
				run={run}
			/>

			<BasicPaginatedTable
				columns={columns}
				data={specs}
				default_page_size="15"
				hidden_columns={[`skipped`, `flaky`, `last_error`, `version`]}
			/>
		</>
	);
}

function RunSegment({ run }) {
	return (
		<SegmentGroup>
			<CustomSegment raised>
				<FlexBox>
					<div>
						<Header>Result</Header>
						<Icon name={run.passed ? `check` : `times`} color={run.passed ? `green` : `red`} size="large" />
					</div>
					<div>
						<Header>Build Key</Header>
						<p>
							<JiraLink issue_key={run.title} />&nbsp;&nbsp;
							<Popup content={run.issue_summary} trigger={<Icon name="info circle" color="teal" />} />
						</p>
					</div>
					{run.site ? (
						<div>
							<Header>Site</Header>
							<a href={run.site ? `https://${run.site}.focus.support/focus` : ``} target="_blank" rel="noreferrer">{run.site ? run.site : `N/A`}</a>
						</div>
					) : null}
					<div>
						<Header>User</Header>
						<p>{run?.issue_user || `N/A`}</p>
					</div>
					<div>
						<Header>Run Date</Header>
						<p>{new Date(`${run.run_date} UTC`).toLocaleTimeString(`en-US`, {
							weekday: `long`, year: `numeric`, month: `short`, day: `numeric`, hour: `2-digit`, minute: `2-digit`, second: `2-digit`, hour12: true
						})}</p>
					</div>
					<div>
						<Header>Suites</Header>
						<p>{run.suites_ran || `N/A`}</p>
					</div>
					<div>
						<Header>Duration</Header>
						<p><Duration duration={run.duration} /></p>
					</div>
					<div>
						<Header>Version</Header>
						<p>{run.version || `N/A`}</p>
					</div>
					<div>
						<Header>Build</Header>
						<a href={run.build_url ? `${run.build_url}/console` : ``} target="_blank" rel="noreferrer">{run.build_url ? run.build_url.replace(/\/$/, ``).split(`/`).at(-1) : ``}</a>
					</div>
				</FlexBox>
			</CustomSegment>

			<CustomSegment>
				<FlexBox>
					<div>
						<Header>Passed</Header>
						<p>{run.total_passed || 0}</p>
					</div>

					<div>
						<Header>Failed</Header>
						<p>{run.total_failed || 0}</p>
					</div>

					<div>
						<Header>Flaky</Header>
						<p>{run.total_flaky || 0}</p>
					</div>

					<div>
						<Header>Skipped</Header>
						<p>{run.total_skipped || 0}</p>
					</div>
				</FlexBox>
			</CustomSegment>
		</SegmentGroup>
	);
}

export default SingleRun;
